import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';

// Landscape orientation (wider than tall)
const PAGE_WIDTH = 842; // A4 Landscape width
const PAGE_HEIGHT = 595; // A4 Landscape height
const MARGIN = 30;
const ROW_HEIGHT = 45;
const HEADER_HEIGHT = 40;
const ITEMS_PER_PAGE = 8;

interface LineItemForPrint {
  name: string;
  nameKor?: string | undefined;
  quantity: number;
}

async function loadKoreanFont(): Promise<ArrayBuffer> {
  const response = await fetch('/fonts/NotoSansKR-Regular.ttf');
  return response.arrayBuffer();
}

async function drawPackingVerificationPage(
  pdfDoc: PDFDocument,
  order: OrderSummary,
  items: LineItemForPrint[],
  pageNumber: number,
  totalPages: number,
  koreanFontBytes: ArrayBuffer,
): Promise<void> {
  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  page.setRotation(degrees(90));
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const koreanFont = await pdfDoc.embedFont(koreanFontBytes);
  const totalQty = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);
  const trackingId = order.parxlData?.plsShipmentReference || order.shippingData?.providerOrderId || '';

  // Title
  page.drawText('PACKING VERIFICATION', {
    x: MARGIN,
    y: PAGE_HEIGHT - 35,
    size: 24,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // Order ID - Large font
  page.drawText(`Order ID: ${order.orderId}`, {
    x: MARGIN,
    y: PAGE_HEIGHT - 65,
    size: 18,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // Tracking & Country
  page.drawText(`Tracking: ${trackingId}`, {
    x: MARGIN + 250,
    y: PAGE_HEIGHT - 65,
    size: 14,
    font,
    color: rgb(0, 0, 0),
  });

  page.drawText(`Country: ${order.consignee?.countryCode || ''}`, {
    x: MARGIN + 550,
    y: PAGE_HEIGHT - 65,
    size: 14,
    font,
    color: rgb(0, 0, 0),
  });

  // Total Qty - Very large
  page.drawText(`Total Qty: ${totalQty}`, {
    x: PAGE_WIDTH - MARGIN - 140,
    y: PAGE_HEIGHT - 35,
    size: 20,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN, y: PAGE_HEIGHT - 80 },
    end: { x: PAGE_WIDTH - MARGIN, y: PAGE_HEIGHT - 80 },
    thickness: 2,
    color: rgb(0, 0, 0),
  });

  const tableWidth = PAGE_WIDTH - 2 * MARGIN;
  const noColWidth = 60;
  const qtyColWidth = 80;
  const itemColWidth = tableWidth - noColWidth - qtyColWidth;

  const headerY = PAGE_HEIGHT - 95;

  page.drawRectangle({
    x: MARGIN,
    y: headerY - HEADER_HEIGHT,
    width: tableWidth,
    height: HEADER_HEIGHT,
    borderColor: rgb(0, 0, 0),
    borderWidth: 2,
    color: rgb(0.85, 0.85, 0.85),
  });

  page.drawText('No.', {
    x: MARGIN + 15,
    y: headerY - 28,
    size: 16,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth, y: headerY },
    end: { x: MARGIN + noColWidth, y: headerY - HEADER_HEIGHT },
    thickness: 2,
    color: rgb(0, 0, 0),
  });

  page.drawText('상품명', {
    x: MARGIN + noColWidth + 10,
    y: headerY - 28,
    size: 16,
    font: koreanFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth + itemColWidth, y: headerY },
    end: { x: MARGIN + noColWidth + itemColWidth, y: headerY - HEADER_HEIGHT },
    thickness: 2,
    color: rgb(0, 0, 0),
  });

  page.drawText('수량', {
    x: MARGIN + noColWidth + itemColWidth + 20,
    y: headerY - 28,
    size: 16,
    font: koreanFont,
    color: rgb(0, 0, 0),
  });

  let currentY = headerY - HEADER_HEIGHT;

  items.forEach((item, index) => {
    const rowY = currentY - ROW_HEIGHT;
    const globalIndex = (pageNumber - 1) * ITEMS_PER_PAGE + index + 1;

    page.drawRectangle({
      x: MARGIN,
      y: rowY,
      width: tableWidth,
      height: ROW_HEIGHT,
      borderColor: rgb(0, 0, 0),
      borderWidth: 1,
    });

    page.drawLine({
      start: { x: MARGIN + noColWidth, y: currentY },
      end: { x: MARGIN + noColWidth, y: rowY },
      thickness: 1,
      color: rgb(0, 0, 0),
    });

    page.drawLine({
      start: { x: MARGIN + noColWidth + itemColWidth, y: currentY },
      end: { x: MARGIN + noColWidth + itemColWidth, y: rowY },
      thickness: 1,
      color: rgb(0, 0, 0),
    });

    // No. - Large font
    page.drawText(globalIndex.toString(), {
      x: MARGIN + 20,
      y: rowY + 15,
      size: 16,
      font: boldFont,
      color: rgb(0, 0, 0),
    });

    // Korean product name only - Large font, Black color
    const productName = item.nameKor || item.name || '';
    const maxChars = 45;
    const displayName = productName.length > maxChars ? productName.substring(0, maxChars) + '...' : productName;

    page.drawText(displayName, {
      x: MARGIN + noColWidth + 10,
      y: rowY + 15,
      size: 14,
      font: koreanFont,
      color: rgb(0, 0, 0),
    });

    // Quantity - Very large font
    page.drawText(item.quantity.toString(), {
      x: MARGIN + noColWidth + itemColWidth + 25,
      y: rowY + 12,
      size: 20,
      font: boldFont,
      color: rgb(0, 0, 0),
    });

    currentY = rowY;
  });

  const totalRowY = currentY - ROW_HEIGHT;
  page.drawRectangle({
    x: MARGIN,
    y: totalRowY,
    width: tableWidth,
    height: ROW_HEIGHT,
    borderColor: rgb(0, 0, 0),
    borderWidth: 3,
    color: rgb(0.9, 0.9, 0.9),
  });

  page.drawText('합계 (TOTAL)', {
    x: MARGIN + noColWidth + itemColWidth / 2 - 60,
    y: totalRowY + 14,
    size: 18,
    font: koreanFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth + itemColWidth, y: currentY },
    end: { x: MARGIN + noColWidth + itemColWidth, y: totalRowY },
    thickness: 2,
    color: rgb(0, 0, 0),
  });

  page.drawText(totalQty.toString(), {
    x: MARGIN + noColWidth + itemColWidth + 20,
    y: totalRowY + 10,
    size: 24,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // Footer
  page.drawText(`Page ${pageNumber} / ${totalPages}`, {
    x: PAGE_WIDTH - MARGIN - 80,
    y: 25,
    size: 12,
    font,
    color: rgb(0.3, 0.3, 0.3),
  });

  page.drawText('[ 검수 완료 후 폐기 ]', {
    x: MARGIN,
    y: 25,
    size: 12,
    font: koreanFont,
    color: rgb(0.6, 0, 0),
  });
}

export async function appendPackingVerificationPages(
  existingPdfBytes: Uint8Array,
  order: OrderSummary,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  pdfDoc.registerFontkit(fontkit);
  const koreanFontBytes = await loadKoreanFont();
  const items = order.lineItems.map((item) => ({
    name: item.name,
    nameKor: item.nameKor,
    quantity: item.quantity,
  }));

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const pagesToCreate = Math.max(1, totalPages);

  for (let i = 0; i < pagesToCreate; i++) {
    const startIdx = i * ITEMS_PER_PAGE;
    const pageItems = items.slice(startIdx, startIdx + ITEMS_PER_PAGE);
    await drawPackingVerificationPage(pdfDoc, order, pageItems, i + 1, pagesToCreate, koreanFontBytes);
  }

  return pdfDoc.save();
}

